/**
 * @file schemas/roles.ts
 * @description 角色管理 Zod 校驗 schema（Task 10）。
 *
 * 供前端角色配置介面與後端端點共用；
 * 後端還會額外做防呆（admin 鎖定 / 內置不刪 / 用戶佔用 / 自鎖）。
 */
import { z } from 'zod';
import { WORKBENCH_TYPES } from '../enums';
/**
 * 建立角色請求 schema。
 *
 * - code：選填，缺省由後端由 name 轉 slug；唯一性衝突回 409 ROLE_CODE_EXISTS。
 * - cloneFrom：選填，指定已存在角色代碼，建立時複製其能力集與菜單黑名單。
 * - isBuiltin 由服務層固定為 false，不接受前端傳入。
 */
export const createRoleSchema = z.object({
    /** 角色代碼（英數字底線連字號，1–50 字元）；省略時後端自動生成 */
    code: z
        .string()
        .min(1)
        .max(50)
        .regex(/^[a-z0-9_-]+$/, '代碼只允許小寫英數字、底線、連字號')
        .optional(),
    /** 顯示名稱 */
    name: z.string().min(1).max(100),
    /** 描述 */
    description: z.string().max(500).optional(),
    /**
     * 工作台類型（必填）。
     * 決定前端佈局殼與菜單基準樹；必須為 WORKBENCH_TYPES 內的合法值。
     */
    workbenchType: z.enum(WORKBENCH_TYPES),
    /** 複製能力集與菜單黑名單的模板角色代碼 */
    cloneFrom: z.string().min(1).max(50).optional(),
});
/**
 * 更新角色請求 schema（所有欄位可選）。
 *
 * PATCH 語義：僅更新有傳的欄位；code/isBuiltin 不可改。
 */
export const updateRoleSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(500).optional(),
    workbenchType: z.enum(WORKBENCH_TYPES).optional(),
    /** active | disabled */
    status: z.enum(['active', 'disabled']).optional(),
});
/**
 * PUT /:code/capabilities 請求 schema（全量替換能力集）。
 *
 * capabilities 每個值必須是合法的能力鍵（由後端對照 ALL_CAPABILITIES 校驗，
 * 此 schema 只保證型別為字串陣列）。
 */
export const putCapabilitiesSchema = z.object({
    /** 全量能力鍵清單；後端會逐項校驗 ∈ ALL_CAPABILITIES */
    capabilities: z.array(z.string().min(1).max(100)),
});
/**
 * PUT /:code/menu-hidden 請求 schema（全量替換菜單黑名單）。
 */
export const putMenuHiddenSchema = z.object({
    /** 全量菜單 key 黑名單 */
    menuKeys: z.array(z.string().min(1).max(100)),
});
